import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addFoodItemToDatabase } from '../data/AsyncFunctions/addFoodItemToDatabase';
import { useAuth } from '../contexts/authContext';
import { CONTAINER_OPTIONS } from '../reducers';

const initialFormState = {
  name: '',
  quantity: '',
  expirationDate: '',
  containerKey: 'fridge_level_contents',
};

function AddItemButton() {
  const dispatch = useDispatch();
  const { userLoggedIn } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(initialFormState);

  const closeModal = () => {
    setIsOpen(false);
    setError('');
    setFormData(initialFormState);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!userLoggedIn) {
      setError('Sign in to add food items.');
      return;
    }

    if (!formData.name.trim() || !formData.quantity.trim() || !formData.expirationDate) {
      setError('Fill out every field before saving.');
      return;
    }

    const newItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      name: formData.name.trim(),
      quantity: formData.quantity.trim(),
      expirationDate: formData.expirationDate,
      containerKey: formData.containerKey,
    };

    try {
      setIsSaving(true);
      setError('');

      await addFoodItemToDatabase(userLoggedIn, newItem);
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          containerKey: newItem.containerKey,
          item: newItem,
        },
      });

      closeModal();
    } catch (submitError) {
      setError('Unable to save this item right now.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="Add food item"
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          width: '64px',
          height: '64px',
          borderRadius: '999px',
          border: 'none',
          backgroundColor: '#198754',
          color: '#fff',
          fontSize: '2rem',
          lineHeight: 1,
          boxShadow: '0 14px 32px rgba(25, 135, 84, 0.35)',
          cursor: 'pointer',
          zIndex: 1000,
        }}
      >
        +
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 1001,
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              width: '100%',
              maxWidth: '420px',
              backgroundColor: '#fff',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '16px' }}>Add Food Item</h2>

            <label style={{ display: 'block', marginBottom: '12px' }}>
              Name
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={{ width: '100%', marginTop: '6px', padding: '10px' }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '12px' }}>
              Quantity
              <input
                type="text"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                style={{ width: '100%', marginTop: '6px', padding: '10px' }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '12px' }}>
              Expiration Date
              <input
                type="date"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleChange}
                style={{ width: '100%', marginTop: '6px', padding: '10px' }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '16px' }}>
              Container
              <select
                name="containerKey"
                value={formData.containerKey}
                onChange={handleChange}
                style={{ width: '100%', marginTop: '6px', padding: '10px' }}
              >
                {CONTAINER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <p style={{ color: '#b42318', marginTop: 0, marginBottom: '16px' }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#fff',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: '#198754',
                  color: '#fff',
                }}
              >
                {isSaving ? 'Saving...' : 'Save Item'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default AddItemButton;
